import { useTranslation } from "react-i18next";
import i18n from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PhoneField } from "@/components/PhoneField";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";

export interface InviteEntryData {
  firstName: string;
  lastName: string;
  email: string;
  customerId: string;
  phone?: string;
  customerHistoryFile?: File | null;
}

interface InviteEntryProps {
  index: number;
  value: InviteEntryData;
  onChange: (data: InviteEntryData) => void;
  onRemove: () => void;
  canRemove: boolean;
  error?: string | null;
  entityLabel?: string; // e.g., "Contractor" or "Team Member"
  idLabel?: string; // e.g., "Contractor ID" or "Team Member ID"
  showCustomerHistory?: boolean; // Whether to show the customer history file field
  showIdField?: boolean; // Whether to show the ID field (default: true)
}

export function InviteEntry({
  index,
  value,
  onChange,
  onRemove,
  canRemove,
  error,
  entityLabel = i18n.t("components.invites.inviteEntry.contractor"),
  idLabel = i18n.t("components.invites.inviteEntry.contractorId"),
  showCustomerHistory = true,
  showIdField = true,
}: InviteEntryProps) {
  const { t } = useTranslation();
  const handleFieldChange = (
    field: keyof InviteEntryData,
    newValue: string | File | null,
  ) => {
    onChange({
      ...value,
      [field]: newValue,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    handleFieldChange("customerHistoryFile", file);
  };

  return (
    <div className="border rounded-lg p-4 space-y-4 bg-card">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">
          {entityLabel} {index + 1}
        </h3>
        {canRemove && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onRemove}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor={`firstName-${index}`}>{t("components.invites.inviteEntry.firstName")}</Label>
          <Input
            id={`firstName-${index}`}
            placeholder={t("components.invites.inviteEntry.enterFirstName")}
            value={value.firstName}
            onChange={(e) => handleFieldChange("firstName", e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor={`lastName-${index}`}>{t("components.invites.inviteEntry.lastName")}</Label>
          <Input
            id={`lastName-${index}`}
            placeholder={t("components.invites.inviteEntry.enterLastName")}
            value={value.lastName}
            onChange={(e) => handleFieldChange("lastName", e.target.value)}
            required
          />
        </div>
      </div>

      <div
        className={`grid gap-4 ${showIdField ? "grid-cols-1 md:grid-cols-2" : "grid-cols-1"}`}
      >
        <div className="space-y-2">
          <Label htmlFor={`email-${index}`}>{t("components.invites.inviteEntry.email")}</Label>
          <Input
            id={`email-${index}`}
            type="email"
            placeholder={t("components.invites.inviteEntry.enterEmail")}
            value={value.email}
            onChange={(e) => handleFieldChange("email", e.target.value)}
            required
          />
        </div>

        {showIdField && (
          <div className="space-y-2">
            <Label htmlFor={`customerId-${index}`}>{idLabel} *</Label>
            <Input
              id={`customerId-${index}`}
              placeholder={`Enter ${idLabel}`}
              value={value.customerId}
              onChange={(e) => handleFieldChange("customerId", e.target.value)}
              required
            />
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor={`phone-${index}`}>{t("components.invites.inviteEntry.phoneOptional")}</Label>
        <PhoneField
          value={value.phone || ""}
          onChange={(val) => handleFieldChange("phone", val)}
        />
      </div>

      {showCustomerHistory && (
        <div className="space-y-2">
          <Label htmlFor={`customerHistory-${index}`}>
            {t("components.invites.inviteEntry.customerHistoryCsvOptional")}
          </Label>
          <Input
            id={`customerHistory-${index}`}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
          />
          {value.customerHistoryFile && (
            <p className="text-sm text-muted-foreground">
              {t("components.invites.inviteEntry.selected")} {value.customerHistoryFile.name}
            </p>
          )}
        </div>
      )}

      {error && (
        <div className="text-sm text-destructive bg-destructive/10 p-2 rounded">
          {error}
        </div>
      )}
    </div>
  );
}
